import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, AlertCircle, Smartphone } from 'lucide-react';
import type { FaceRecognitionPlatformBreakdown } from '../../api/face-recognition.schemas';

interface PlatformBreakdownChartProps {
  data?: FaceRecognitionPlatformBreakdown;
  loading?: boolean;
  error?: Error;
  onRetry?: () => void;
}

/**
 * PlatformBreakdownChart Component
 * 
 * Displays a chart showing Face Recognition platform breakdown.
 * Shows Web/Android/iOS platform distribution.
 * Only displayed when breakdown data is available from BFF.
 * 
 * Requirements: 2.7, 6.2
 */
export function PlatformBreakdownChart({ 
  data, 
  loading = false, 
  error,
  onRetry
}: PlatformBreakdownChartProps) {
  
  // Loading state
  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Platform Distribution</CardTitle>
          </div>
          <CardDescription>Web, Android, and iOS usage</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 p-6 pt-0 space-y-4">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-3 h-3 rounded-full" />
                    <Skeleton className="h-4 w-12" />
                    <Skeleton className="h-4 w-4" />
                  </div>
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="w-full h-3 rounded-full" />
              </div>
            ))}
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <Skeleton className="h-4 w-24 mb-2" />
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="text-center space-y-1">
                  <Skeleton className="h-4 w-8 mx-auto" />
                  <Skeleton className="h-3 w-12 mx-auto" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Platform Distribution</CardTitle>
          </div>
          <CardDescription>Failed to load platform data</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Unable to display chart</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
            {onRetry && (
              <Button onClick={onRetry} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Platform Distribution</CardTitle>
          </div>
          <CardDescription>Web, Android, and iOS usage</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Smartphone className="h-6 w-6 mx-auto mb-2" />
            <p>No platform data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate total and percentages
  const total = data.web + data.android + data.ios;
  const platforms = [
    { name: 'Web', value: data.web, color: 'bg-blue-500', textColor: 'text-blue-700' },
    { name: 'Android', value: data.android, color: 'bg-green-500', textColor: 'text-green-700' },
    { name: 'iOS', value: data.ios, color: 'bg-gray-500', textColor: 'text-gray-700' },
  ].filter(item => item.value > 0); // Only show non-zero values

  if (total === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Platform Distribution</CardTitle>
          </div>
          <CardDescription>Web, Android, and iOS usage</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Smartphone className="h-6 w-6 mx-auto mb-2" />
            <p>No platform data recorded</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Platform Distribution</CardTitle>
        </div>
        <CardDescription>
          {total.toLocaleString()} total operations
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-4 overflow-y-auto">
          {platforms.map((item, index) => {
            const percentage = total > 0 ? (item.value / total) * 100 : 0;
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${item.color}`} />
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{item.value.toLocaleString()}</span>
                    <span className="text-muted-foreground ml-1">({percentage.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className={`${item.color} h-3 rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Summary */}
        <div className="mt-4 p-3 bg-muted/50 rounded-lg flex-shrink-0">
          <h4 className="font-medium text-sm mb-2">Platform Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div className="text-center">
              <div className="font-semibold text-blue-600">{data.web}</div>
              <div className="text-muted-foreground">Web</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-green-600">{data.android}</div>
              <div className="text-muted-foreground">Android</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-600">{data.ios}</div>
              <div className="text-muted-foreground">iOS</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}